import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Sparkles, Loader2, Copy, Download, Megaphone, Image as ImageIcon, Mail, Newspaper,
  Lightbulb, Send, Users2, Store, Building2, Facebook, Linkedin, MessageCircle,
  UploadCloud, CheckCircle2, AlertTriangle, ArrowRight, Bot, User as UserIcon,
  Paperclip, FileText, Film, X, Search, UserPlus, Palette, History as HistoryIcon, Trash2,
  Eye, Save, Clock, Monitor, Smartphone, RefreshCw, MousePointerClick, MailOpen, BarChart3,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList } from "recharts";
import { toast } from "sonner";
import FbGroupShare from "@/components/FbGroupShare";

const AUDIENCES = [
  { value: "consumer", label: "Consommateurs", Icon: Users2 },
  { value: "pro", label: "Partenaires pros", Icon: Store },
  { value: "organization", label: "Organismes & CSE", Icon: Building2 },
];
const ROLE_LABELS = {
  particular: "Particuliers / bénéficiaires",
  member: "Membres d'organisme",
  professional: "Commerçants partenaires",
  organization: "Organismes & CSE",
};
const EMAIL_THEMES = [
  { value: "newsletter", label: "Newsletter", desc: "Bannière + blocs de bons plans" },
  { value: "announcement", label: "Annonce", desc: "Grande bannière + bouton d'action" },
  { value: "two_columns", label: "2 colonnes", desc: "Texte + image côte à côte" },
  { value: "prospection", label: "Prospection", desc: "Mise en avant B2B + bouton" },
  { value: "simple", label: "Texte simple", desc: "Sobre et épuré" },
];
const FREQ_OPTS = [
  { value: "", label: "Aucune (envoi unique)" },
  { value: "daily", label: "Chaque jour" },
  { value: "weekly", label: "Chaque semaine" },
  { value: "monthly", label: "Chaque mois" },
];
const FREQ_LABELS = { daily: "Quotidienne", weekly: "Hebdomadaire", monthly: "Mensuelle" };
const CONTACT_CATS = [
  { value: "particular", label: "Particuliers" },
  { value: "professional", label: "Pros" },
  { value: "organization", label: "Organismes" },
  { value: "other", label: "Autres" },
];

function copyText(txt) {
  navigator.clipboard.writeText(txt).then(
    () => toast.success("Copié dans le presse-papier !"),
    () => toast.message("Copie manuelle requise.")
  );
}

async function pollJob(jobId, onDone, onError) {
  let tries = 0;
  const tick = async () => {
    tries += 1;
    try {
      const { data } = await api.get(`/admin/ai-studio/jobs/${jobId}`);
      if (data.status === "done") return onDone(data.result || {});
      if (data.status === "error") return onError(data.error || "Génération indisponible.");
    } catch (e) { void e; }
    if (tries > 45) return onError("Délai dépassé, réessayez.");
    setTimeout(tick, 4000);
  };
  setTimeout(tick, 3500);
}

/* ====================== AGENT RÉSEAUX SOCIAUX ====================== */
function SocialAgent({ reuse, onConsumed }) {
  const [audience, setAudience] = useState("consumer");
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [post, setPost] = useState(null);
  const [showGroups, setShowGroups] = useState(false);

  useEffect(() => {
    if (!reuse?.data) return;
    const r = reuse.data;
    setPrompt(r.prompt || ""); setAudience(r.audience || "consumer");
    if (r.text) setPost({ text: r.text, hashtags: r.hashtags || [], image_url: r.image_url || "" });
    toast.success("Publication chargée — relancez ou modifiez à votre guise.");
    onConsumed?.();
  }, [reuse]); // eslint-disable-line

  const run = async () => {
    if (prompt.trim().length < 5) { toast.error("Décrivez votre demande (sujet du post)."); return; }
    setBusy(true); setPost(null);
    try {
      const { data } = await api.post("/admin/ai-studio/jobs", { kind: "social", prompt, audience });
      toast.info("Rédaction + visuel en cours…");
      pollJob(data.job_id,
        (res) => { setPost(res); setBusy(false); toast.success("Publication prête !"); },
        (err) => { toast.error(err); setBusy(false); });
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };

  const full = post ? [post.text, (post.hashtags || []).join(" ")].filter(Boolean).join("\n\n") : "";
  const shareUrl = window.location.origin;

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="font-semibold flex items-center gap-2"><Megaphone size={18} className="text-[#E05638]" /> Rédiger une publication</h3>
        <label className="block text-sm font-medium text-slate-600 mt-4 mb-1.5">Cible</label>
        <div className="flex flex-wrap gap-2" data-testid="social-audience">
          {AUDIENCES.map((a) => (
            <button key={a.value} onClick={() => setAudience(a.value)} data-testid={`social-aud-${a.value}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold border ${audience === a.value ? "bg-[#E05638] text-white border-[#E05638]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
              <a.Icon size={14} /> {a.label}
            </button>
          ))}
        </div>
        <label className="block text-sm font-medium text-slate-600 mt-4 mb-1.5">Votre demande</label>
        <Textarea rows={5} value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="social-prompt"
          placeholder="Ex : annonce la Fête du commerce local à Chartres ce samedi, ton chaleureux, invite à profiter des bons plans." />
        <Button onClick={run} disabled={busy} className="btn-shine text-white mt-4 w-full" data-testid="social-generate">
          {busy ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Génération…</> : <><Sparkles size={15} className="mr-1.5" /> Générer la publication</>}
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 min-h-[200px]">
        {!post && !busy && <p className="text-sm text-slate-400 text-center py-16" data-testid="social-empty">La publication générée apparaîtra ici.</p>}
        {busy && <div className="py-16 text-center text-slate-400"><Loader2 className="inline animate-spin mr-2" /> Rédaction + visuel…</div>}
        {post && (
          <div data-testid="social-result">
            {post.image_url && <img src={post.image_url} alt="visuel" className="w-full aspect-square object-cover rounded-xl bg-slate-100 mb-3" />}
            <p className="text-sm text-slate-700 whitespace-pre-line">{post.text}</p>
            {(post.hashtags || []).length > 0 && <div className="mt-2 text-xs font-semibold text-[#1877F2] break-words">{post.hashtags.join(" ")}</div>}
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={() => copyText(full)} size="sm" className="btn-shine text-white" data-testid="social-copy"><Copy size={14} className="mr-1.5" /> Copier</Button>
              {post.image_url && <a href={post.image_url} download target="_blank" rel="noreferrer"><Button size="sm" variant="outline" data-testid="social-download"><Download size={14} className="mr-1.5" /> Visuel</Button></a>}
              <button onClick={() => { copyText(full); window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(full)}`, "_blank", "noopener"); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1877F2] text-white text-xs font-semibold" data-testid="social-fb"><Facebook size={14} /> Facebook</button>
              <button onClick={() => { copyText(full); window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, "_blank", "noopener"); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0A66C2] text-white text-xs font-semibold" data-testid="social-linkedin"><Linkedin size={14} /> LinkedIn</button>
              <button onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(full + "\n" + shareUrl)}`, "_blank", "noopener")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-semibold" data-testid="social-whatsapp"><MessageCircle size={14} /> WhatsApp</button>
              <button onClick={() => setShowGroups((v) => !v)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 text-white text-xs font-semibold" data-testid="social-groups"><Users2 size={14} /> Groupes FB</button>
            </div>
            {showGroups && <FbGroupShare text={full} shareUrl={shareUrl} />}
          </div>
        )}
      </div>
    </div>
  );
}

/* ====================== AGENT IMAGE ====================== */
function ImageAgent({ reuse, onConsumed }) {
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("photo");
  const [square, setSquare] = useState(true);
  const [busy, setBusy] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [resultSquare, setResultSquare] = useState(true);

  useEffect(() => {
    if (!reuse?.data) return;
    const r = reuse.data;
    setPrompt(r.prompt || "");
    if (r.style) setStyle(r.style);
    if (typeof r.square === "boolean") setSquare(r.square);
    if (r.image_url) { setImageUrl(r.image_url); setResultSquare(r.square !== false); }
    toast.success("Image chargée — téléchargez-la ou régénérez une variante.");
    onConsumed?.();
  }, [reuse]); // eslint-disable-line

  const run = async () => {
    if (prompt.trim().length < 5) { toast.error("Décrivez l'image souhaitée."); return; }
    setBusy(true); setImageUrl("");
    try {
      const { data } = await api.post("/admin/ai-studio/jobs", { kind: "image", prompt, style, square });
      toast.info("Création de l'image en cours…");
      pollJob(data.job_id,
        (res) => { setImageUrl(res.image_url || ""); setResultSquare(square); setBusy(false); toast.success("Image prête !"); },
        (err) => { toast.error(err); setBusy(false); });
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };

  const Pill = ({ active, onClick, children, testid }) => (
    <button onClick={onClick} data-testid={testid}
      className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${active ? "bg-[#E05638] text-white border-[#E05638]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{children}</button>
  );

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="font-semibold flex items-center gap-2"><ImageIcon size={18} className="text-[#E05638]" /> Générer une image</h3>
        <label className="block text-sm font-medium text-slate-600 mt-4 mb-1.5">Description</label>
        <Textarea rows={5} value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="image-prompt"
          placeholder="Ex : une rue commerçante animée de Chartres au printemps, terrasses de café, ambiance chaleureuse." />
        <label className="block text-sm font-medium text-slate-600 mt-4 mb-1.5">Style</label>
        <div className="flex gap-2" data-testid="image-style">
          <Pill active={style === "photo"} onClick={() => setStyle("photo")} testid="image-style-photo">Photo réaliste</Pill>
          <Pill active={style === "illustration"} onClick={() => setStyle("illustration")} testid="image-style-illustration">Illustration</Pill>
        </div>
        <label className="block text-sm font-medium text-slate-600 mt-4 mb-1.5">Format</label>
        <div className="flex gap-2" data-testid="image-format">
          <Pill active={square} onClick={() => setSquare(true)} testid="image-format-square">Carré (réseaux)</Pill>
          <Pill active={!square} onClick={() => setSquare(false)} testid="image-format-landscape">Paysage (bannière)</Pill>
        </div>
        <Button onClick={run} disabled={busy} className="btn-shine text-white mt-4 w-full" data-testid="image-generate">
          {busy ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Création…</> : <><Sparkles size={15} className="mr-1.5" /> Générer l'image</>}
        </Button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-5 min-h-[200px] grid place-items-center">
        {!imageUrl && !busy && <p className="text-sm text-slate-400 text-center" data-testid="image-empty">L'image générée apparaîtra ici.</p>}
        {busy && <div className="text-center text-slate-400"><Loader2 className="inline animate-spin mr-2" /> Création de l'image…</div>}
        {imageUrl && (
          <div className="w-full" data-testid="image-result">
            <img src={imageUrl} alt="générée" className={`w-full rounded-xl bg-slate-100 ${resultSquare ? "aspect-square object-cover" : "aspect-[1.91/1] object-contain"}`} />
            <a href={imageUrl} download target="_blank" rel="noreferrer"><Button className="btn-shine text-white mt-3 w-full" data-testid="image-download"><Download size={14} className="mr-1.5" /> Télécharger</Button></a>
          </div>
        )}
      </div>
    </div>
  );
}

/* ====================== AGENT E-MAILS ====================== */
const EmailThumb = ({ item, onRemove, testid }) => (
  <div className="relative group" data-testid={testid}>
    {item.kind === "image"
      ? <img src={item.url} alt={item.filename} className="w-20 h-20 object-cover rounded-lg border border-slate-200" />
      : <div className="w-20 h-20 rounded-lg border border-slate-200 bg-slate-50 grid place-items-center text-slate-500">
          {item.kind === "video" ? <Film size={22} /> : <FileText size={22} />}
        </div>}
    <button onClick={onRemove} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-rose-600 text-white grid place-items-center shadow" data-testid={`${testid}-remove`}><X size={13} /></button>
    <div className="text-[10px] text-slate-500 truncate w-20 mt-0.5">{item.filename}</div>
  </div>
);

function EmailAgent({ reuse, onConsumed }) {
  const [audience, setAudience] = useState({ roles: [], cities: [] });
  const [draftPrompt, setDraftPrompt] = useState("");
  const [draftAud, setDraftAud] = useState("consumer");
  const [drafting, setDrafting] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [theme, setTheme] = useState("newsletter");
  const [banner, setBanner] = useState(null);       // {url,filename}
  const [images, setImages] = useState([]);          // [{url,filename,kind}]
  const [files, setFiles] = useState([]);            // [{url,filename,kind}]
  const [ctaLabel, setCtaLabel] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [source, setSource] = useState("filter");    // filter | member | list
  const [role, setRole] = useState("");
  const [city, setCity] = useState("");
  const [memberQ, setMemberQ] = useState("");
  const [memberResults, setMemberResults] = useState([]);
  const [member, setMember] = useState(null);
  const [emails, setEmails] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [run, setRun] = useState(null);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState("");
  const bannerRef = useRef(null);
  const imgRef = useRef(null);
  const docRef = useRef(null);
  const csvRef = useRef(null);
  const [scheduledAt, setScheduledAt] = useState("");
  const [frequency, setFrequency] = useState("");
  const [templates, setTemplates] = useState([]);
  const [savingTpl, setSavingTpl] = useState(false);
  const [scheduledList, setScheduledList] = useState([]);
  const [recurringList, setRecurringList] = useState([]);
  const [preview, setPreview] = useState({ open: false, html: "", loading: false, device: "desktop" });
  const [contactCat, setContactCat] = useState("");
  const [contacts, setContacts] = useState({ items: [], counts: {}, total: 0 });
  const [showContacts, setShowContacts] = useState(false);
  const [newContact, setNewContact] = useState({ email: "", name: "", category: "other" });

  const loadTemplates = () => api.get("/admin/ai-studio/email/templates").then((r) => setTemplates(r.data || [])).catch(() => {});
  const loadScheduled = () => api.get("/admin/ai-studio/email/scheduled").then((r) => setScheduledList(r.data || [])).catch(() => {});
  const loadRecurring = () => api.get("/admin/ai-studio/email/recurring").then((r) => setRecurringList(r.data || [])).catch(() => {});
  const loadContacts = (cat) => api.get(`/admin/ai-studio/contacts${cat ? `?category=${cat}` : ""}`).then((r) => setContacts(r.data || { items: [], counts: {}, total: 0 })).catch(() => {});
  const addContact = async () => {
    if (!newContact.email.includes("@")) { toast.error("E-mail invalide."); return; }
    try { await api.post("/admin/ai-studio/contacts", newContact); toast.success("Contact ajouté."); setNewContact({ email: "", name: "", category: "other" }); loadContacts(contactCat); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const setContactCategory = async (c, cat) => { try { await api.patch(`/admin/ai-studio/contacts/${c.id}`, { category: cat }); loadContacts(contactCat); } catch (e) { toast.error(formatApiError(e)); } };
  const removeContact = async (id) => { try { await api.delete(`/admin/ai-studio/contacts/${id}`); loadContacts(contactCat); } catch (e) { toast.error(formatApiError(e)); } };

  const toggleRecurring = async (c) => {
    try { await api.patch(`/admin/ai-studio/email/recurring/${c.id}`, { status: c.status === "active" ? "paused" : "active" }); loadRecurring(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteRecurring = async (id) => {
    if (!window.confirm("Supprimer cette campagne récurrente ?")) return;
    try { await api.delete(`/admin/ai-studio/email/recurring/${id}`); toast.success("Campagne récurrente supprimée."); loadRecurring(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    api.get("/admin/ai-studio/email/audience").then((r) => setAudience(r.data)).catch(() => {});
    loadTemplates(); loadScheduled(); loadRecurring(); loadContacts("");
  }, []);

  useEffect(() => {
    if (!reuse?.data) return;
    const r = reuse.data;
    if (r.subject !== undefined) setSubject(r.subject || "");
    if (r.body !== undefined) setBody(r.body || "");
    if (r.theme) setTheme(r.theme);
    if (r.cta_label !== undefined) setCtaLabel(r.cta_label || "");
    if (r.cta_url !== undefined) setCtaUrl(r.cta_url || "");
    if ("banner_url" in r) setBanner(r.banner_url ? { url: r.banner_url, filename: "bannière", kind: "image" } : null);
    if ("image_urls" in r) setImages((r.image_urls || []).map((u) => ({ url: u, filename: "photo", kind: "image" })));
    if ("files" in r) setFiles(r.files || []);
    if (r.source) {
      setSource(r.source); setRole(r.role || ""); setCity(r.city || "");
      if (r.source === "list" && Array.isArray(r.emails)) setEmails(r.emails.join("\n"));
    }
    setScheduledAt(""); setFrequency("");
    toast.success("Contenu chargé — modifiez-le puis renvoyez-le.");
    onConsumed?.();
  }, [reuse]); // eslint-disable-line

  const buildOpts = () => ({
    theme, banner_url: banner?.url || "", cta_label: ctaLabel, cta_url: ctaUrl,
    image_urls: images.map((i) => i.url), files,
  });

  const openPreview = async () => {
    if (body.trim().length < 2) { toast.error("Écrivez d'abord le message."); return; }
    setPreview((p) => ({ ...p, open: true, loading: true }));
    try {
      const { data } = await api.post("/admin/ai-studio/email/preview", { body, ...buildOpts() });
      setPreview((p) => ({ ...p, html: data.html || "", loading: false }));
    } catch (e) { toast.error(formatApiError(e)); setPreview((p) => ({ ...p, open: false, loading: false })); }
  };

  const saveTemplate = async () => {
    const name = window.prompt("Nom du modèle :", subject || "Mon modèle");
    if (!name || name.trim().length < 2) return;
    setSavingTpl(true);
    try {
      await api.post("/admin/ai-studio/email/templates", { name: name.trim(), subject, body, ...buildOpts() });
      toast.success("Modèle enregistré."); loadTemplates();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingTpl(false); }
  };

  const applyTemplate = (t) => {
    if (!t) return;
    setSubject(t.subject || ""); setBody(t.body || ""); setTheme(t.theme || "simple");
    setCtaLabel(t.cta_label || ""); setCtaUrl(t.cta_url || "");
    setBanner(t.banner_url ? { url: t.banner_url, filename: "bannière", kind: "image" } : null);
    setImages((t.image_urls || []).map((u) => ({ url: u, filename: "photo", kind: "image" })));
    setFiles(t.files || []);
    toast.success(`Modèle « ${t.name} » chargé.`);
  };

  const deleteTemplate = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer ce modèle ?")) return;
    try { await api.delete(`/admin/ai-studio/email/templates/${id}`); loadTemplates(); } catch (err) { toast.error(formatApiError(err)); }
  };

  const cancelScheduled = async (id) => {
    if (!window.confirm("Annuler cet envoi programmé ?")) return;
    try { await api.delete(`/admin/ai-studio/email/scheduled/${id}`); toast.success("Envoi annulé."); loadScheduled(); } catch (e) { toast.error(formatApiError(e)); }
  };

  // Recherche de membre (débounce simple)
  useEffect(() => {
    if (source !== "member" || memberQ.trim().length < 2) { setMemberResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/admin/ai-studio/members?q=${encodeURIComponent(memberQ.trim())}`)
        .then((r) => setMemberResults(r.data || [])).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [memberQ, source]);

  const draft = async () => {
    if (draftPrompt.trim().length < 5) { toast.error("Décrivez l'e-mail souhaité."); return; }
    setDrafting(true);
    try {
      const { data } = await api.post("/admin/ai-studio/email/draft", { prompt: draftPrompt, audience: draftAud });
      setSubject(data.subject || ""); setBody(data.body || "");
      toast.success("Brouillon rédigé — relisez avant d'envoyer.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDrafting(false); }
  };

  const uploadFile = async (file, slot) => {
    const okImg = file.type.startsWith("image/");
    if (slot === "banner" && !okImg) { toast.error("La bannière doit être une image."); return; }
    setUploading(slot);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/ai-studio/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const item = { url: data.url, filename: data.filename, kind: data.kind };
      if (slot === "banner") setBanner(item);
      else if (slot === "image") setImages((p) => [...p, item]);
      else setFiles((p) => [...p, item]);
      toast.success(`${data.filename} ajouté.`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(""); }
  };
  const onPick = (slot, ref) => async (e) => {
    const list = [...(e.target.files || [])];
    for (const f of list) await uploadFile(f, slot);
    if (ref.current) ref.current.value = "";
  };

  const parsedEmails = emails.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter((e) => e.includes("@"));
  const uniqueEmails = [...new Set(parsedEmails)];

  const onCsv = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const txt = await f.text();
    const found = txt.split(/[\s,;\n]+/).map((x) => x.trim().toLowerCase()).filter((x) => x.includes("@"));
    setEmails([...new Set(found)].join("\n"));
    toast.success(`${new Set(found).size} e-mail(s) importé(s) depuis le CSV.`);
    if (csvRef.current) csvRef.current.value = "";
  };

  const estimate = () => {
    if (source === "contacts") return contactCat ? (contacts.counts[contactCat] || 0) : (contacts.total || 0);
    if (source === "member") return member ? 1 : 0;
    if (source === "list") return uniqueEmails.length;
    if (role) { const r = audience.roles.find((x) => x.role === role); return r ? r.count : 0; }
    return audience.roles.reduce((s, x) => s + x.count, 0);
  };

  const openConfirm = () => {
    if (subject.trim().length < 2 || body.trim().length < 5) { toast.error("Objet et message requis."); return; }
    if (source === "member" && !member) { toast.error("Choisissez un membre destinataire."); return; }
    if (source === "list" && uniqueEmails.length === 0) { toast.error("Aucune adresse e-mail valide."); return; }
    if (source === "contacts" && estimate() === 0) { toast.error("Aucun contact dans cette catégorie."); return; }
    if (scheduledAt && new Date(scheduledAt) <= new Date()) { toast.error("La date d'envoi doit être dans le futur."); return; }
    if (frequency && !scheduledAt) { toast.error("Choisissez la date et l'heure du premier envoi récurrent."); return; }
    setConfirm(true);
  };
  const minDateTime = (() => { const d = new Date(Date.now() - new Date().getTimezoneOffset() * 60000); return d.toISOString().slice(0, 16); })();

  const send = async () => {
    setConfirm(false); setSending(true); setRun(null);
    const apiSource = source === "filter" ? "filter" : "list";
    let outEmails = source === "member" ? (member ? [member.email] : []) : (source === "list" ? uniqueEmails : []);
    const schedISO = scheduledAt ? new Date(scheduledAt).toISOString() : "";
    try {
      if (source === "contacts") {
        const { data: ce } = await api.get(`/admin/ai-studio/contacts/emails${contactCat ? `?category=${contactCat}` : ""}`);
        outEmails = ce.emails || [];
        if (outEmails.length === 0) { toast.error("Aucun contact dans cette catégorie."); setSending(false); return; }
      }
      if (frequency) {
        const { data } = await api.post("/admin/ai-studio/email/recurring", {
          name: subject, subject, body, first_run: schedISO, frequency,
          source: apiSource, role: source === "filter" ? role : "", city: source === "filter" ? city : "",
          emails: outEmails, ...buildOpts(),
        });
        setSending(false);
        toast.success(`Campagne récurrente créée (${FREQ_LABELS[frequency]}). Prochain envoi : ${new Date(data.next_run).toLocaleString("fr-FR")}.`);
        loadRecurring(); setScheduledAt(""); setFrequency("");
        return;
      }
      const payload = {
        subject, body, source: apiSource, role: source === "filter" ? role : "", city: source === "filter" ? city : "",
        emails: outEmails, ...buildOpts(), scheduled_at: schedISO,
      };
      const { data } = await api.post("/admin/ai-studio/email/send", payload);
      if (data.scheduled) {
        setSending(false);
        toast.success(`E-mail programmé pour le ${new Date(data.scheduled_at).toLocaleString("fr-FR")} (jusqu'à ${data.total} destinataire(s)).`);
        loadScheduled(); setScheduledAt("");
        return;
      }
      toast.info(`Envoi lancé vers ${data.total} destinataire(s)…`);
      let tries = 0;
      const tick = async () => {
        tries += 1;
        try {
          const { data: st } = await api.get(`/admin/ai-studio/email/status/${data.run_id}`);
          setRun(st);
          if (st.status === "done") {
            setSending(false);
            loadContacts(contactCat);
            if ((st.sent || 0) === 0 && (st.failed || 0) > 0) toast.error(`Échec de l'envoi (${st.failed} échec(s)). Rien n'a été envoyé.`);
            else toast.success(`Campagne terminée : ${st.sent} envoyé(s)${st.failed ? `, ${st.failed} échec(s)` : ""}.`);
            return;
          }
        } catch (e) { void e; }
        if (tries > 120) { setSending(false); return; }
        setTimeout(tick, 3000);
      };
      setTimeout(tick, 2500);
    } catch (e) { toast.error(formatApiError(e)); setSending(false); }
  };

  const Thumb = EmailThumb;

  return (
    <div className="space-y-6">
      {/* Rédaction IA */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="font-semibold flex items-center gap-2"><Sparkles size={18} className="text-[#E05638]" /> Rédiger l'e-mail par IA (optionnel)</h3>
        <div className="grid md:grid-cols-[1fr_auto] gap-3 mt-3">
          <Textarea rows={2} value={draftPrompt} onChange={(e) => setDraftPrompt(e.target.value)} data-testid="email-draft-prompt"
            placeholder="Ex : newsletter d'octobre, mets en avant 3 nouveaux commerces partenaires et l'offre fondateur." />
          <div className="flex md:flex-col gap-2">
            <select value={draftAud} onChange={(e) => setDraftAud(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm" data-testid="email-draft-audience">
              {AUDIENCES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
            <Button onClick={draft} disabled={drafting} variant="outline" data-testid="email-draft-btn">
              {drafting ? <Loader2 size={15} className="animate-spin" /> : <>Rédiger</>}
            </Button>
          </div>
        </div>
      </div>

      {/* Thème */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="font-semibold flex items-center gap-2"><Palette size={18} className="text-[#E05638]" /> Thème de l'e-mail</h3>
        <div className="flex flex-wrap gap-2 mt-3" data-testid="email-themes">
          {EMAIL_THEMES.map((t) => (
            <button key={t.value} onClick={() => setTheme(t.value)} data-testid={`email-theme-${t.value}`}
              className={`text-left px-3 py-2 rounded-xl border w-[150px] ${theme === t.value ? "bg-orange-50 border-[#E05638]" : "bg-white border-slate-200 hover:border-slate-300"}`}>
              <div className={`text-sm font-semibold ${theme === t.value ? "text-[#E05638]" : "text-slate-700"}`}>{t.label}</div>
              <div className="text-[11px] text-slate-500 leading-tight mt-0.5">{t.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Contenu */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
        <h3 className="font-semibold flex items-center gap-2"><Mail size={18} className="text-[#E05638]" /> Contenu de l'e-mail</h3>
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1.5">Objet</label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject" placeholder="Objet de l'e-mail" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1.5">Message</label>
          <Textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)} data-testid="email-body" placeholder="Corps de l'e-mail…" />
          <p className="text-xs text-slate-400 mt-1">Un lien de désinscription est ajouté automatiquement en bas de chaque e-mail.</p>
        </div>

        {(theme === "announcement" || theme === "prospection" || theme === "two_columns") && (
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">Texte du bouton (optionnel)</label>
              <Input value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)} data-testid="email-cta-label" placeholder="Ex : Voir les bons plans" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">Lien du bouton</label>
              <Input value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} data-testid="email-cta-url" placeholder="https://proxireduc-28.com/…" />
            </div>
          </div>
        )}
      </div>

      {/* Pièces jointes */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <h3 className="font-semibold flex items-center gap-2"><Paperclip size={18} className="text-[#E05638]" /> Photos, vidéos & documents</h3>

        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1.5">Bannière (image en-tête)</label>
          <input ref={bannerRef} type="file" accept="image/*" onChange={onPick("banner", bannerRef)} className="hidden" data-testid="email-banner-input" />
          {banner
            ? <div className="flex items-center gap-3"><img src={banner.url} alt="bannière" className="h-16 rounded-lg border border-slate-200" /><Button variant="outline" size="sm" onClick={() => setBanner(null)} data-testid="email-banner-remove"><X size={14} className="mr-1" /> Retirer</Button></div>
            : <Button variant="outline" size="sm" onClick={() => bannerRef.current?.click()} disabled={uploading === "banner"} data-testid="email-banner-btn">{uploading === "banner" ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <ImageIcon size={14} className="mr-1.5" />} Choisir une bannière</Button>}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1.5">Photos dans le corps</label>
          <input ref={imgRef} type="file" accept="image/*" multiple onChange={onPick("image", imgRef)} className="hidden" data-testid="email-image-input" />
          <div className="flex flex-wrap gap-3 items-start">
            {images.map((im, i) => <Thumb key={i} item={im} onRemove={() => setImages((p) => p.filter((_, j) => j !== i))} testid={`email-image-${i}`} />)}
            <Button variant="outline" size="sm" onClick={() => imgRef.current?.click()} disabled={uploading === "image"} data-testid="email-image-btn">{uploading === "image" ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <UploadCloud size={14} className="mr-1.5" />} Ajouter des photos</Button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1.5">Documents & vidéos (bouton de téléchargement dans l'e-mail)</label>
          <input ref={docRef} type="file" accept="video/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.txt,.csv" multiple onChange={onPick("doc", docRef)} className="hidden" data-testid="email-doc-input" />
          <div className="flex flex-wrap gap-3 items-start">
            {files.map((f, i) => <Thumb key={i} item={f} onRemove={() => setFiles((p) => p.filter((_, j) => j !== i))} testid={`email-file-${i}`} />)}
            <Button variant="outline" size="sm" onClick={() => docRef.current?.click()} disabled={uploading === "doc"} data-testid="email-doc-btn">{uploading === "doc" ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Paperclip size={14} className="mr-1.5" />} Ajouter un fichier</Button>
          </div>
          <p className="text-xs text-slate-400 mt-1.5">Les fichiers sont hébergés en sécurité ; un bouton de téléchargement est inséré dans l'e-mail (compatible avec toutes les messageries). Max 50 Mo/fichier.</p>
        </div>
      </div>

      {/* Outils : aperçu & modèles */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={openPreview} data-testid="email-preview-btn"><Eye size={14} className="mr-1.5" /> Aperçu (ordi + mobile)</Button>
          <Button variant="outline" size="sm" onClick={saveTemplate} disabled={savingTpl} data-testid="email-tpl-save"><Save size={14} className="mr-1.5" /> Enregistrer comme modèle</Button>
        </div>
        {templates.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="email-tpl-list">
            <span className="text-xs text-slate-500 self-center">Charger un modèle :</span>
            {templates.map((t) => (
              <span key={t.id} className="inline-flex items-center gap-1 pl-3 pr-1.5 py-1 rounded-full bg-slate-100 text-sm" data-testid={`email-tpl-${t.id}`}>
                <button onClick={() => applyTemplate(t)} className="font-medium text-slate-700 hover:text-[#E05638]" data-testid={`email-tpl-load-${t.id}`}>{t.name}</button>
                <button onClick={(e) => deleteTemplate(t.id, e)} className="w-5 h-5 grid place-items-center rounded-full hover:bg-rose-100 text-slate-400 hover:text-rose-600" data-testid={`email-tpl-del-${t.id}`}><X size={12} /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Destinataires */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
        <h3 className="font-semibold flex items-center gap-2"><Users2 size={18} className="text-[#E05638]" /> Destinataires</h3>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setSource("filter")} data-testid="email-src-filter" className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${source === "filter" ? "bg-[#E05638] text-white border-[#E05638]" : "bg-white text-slate-600 border-slate-200"}`}>Groupe de membres</button>
          <button onClick={() => setSource("member")} data-testid="email-src-member" className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${source === "member" ? "bg-[#E05638] text-white border-[#E05638]" : "bg-white text-slate-600 border-slate-200"}`}>Un membre précis</button>
          <button onClick={() => setSource("list")} data-testid="email-src-list" className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${source === "list" ? "bg-[#E05638] text-white border-[#E05638]" : "bg-white text-slate-600 border-slate-200"}`}>Adresse(s) libre(s) / CSV</button>
          <button onClick={() => { setSource("contacts"); loadContacts(contactCat); }} data-testid="email-src-contacts" className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${source === "contacts" ? "bg-[#E05638] text-white border-[#E05638]" : "bg-white text-slate-600 border-slate-200"}`}>Contacts enregistrés</button>
        </div>

        {source === "filter" && (
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">Type de membre</label>
              <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full border border-slate-200 rounded-lg px-2 py-2 text-sm" data-testid="email-role">
                <option value="">Tous les membres ({audience.roles.reduce((s, x) => s + x.count, 0)})</option>
                {audience.roles.map((r) => <option key={r.role} value={r.role}>{ROLE_LABELS[r.role] || r.label} ({r.count})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">Ville (optionnel)</label>
              <select value={city} onChange={(e) => setCity(e.target.value)} className="w-full border border-slate-200 rounded-lg px-2 py-2 text-sm" data-testid="email-city">
                <option value="">Toutes les villes</option>
                {audience.cities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
        )}

        {source === "member" && (
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1.5">Rechercher un membre (nom ou e-mail)</label>
            {member ? (
              <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2" data-testid="email-member-selected">
                <UserPlus size={16} className="text-emerald-600" />
                <div className="min-w-0 flex-1"><div className="font-semibold text-sm truncate">{member.name}</div><div className="text-xs text-slate-500 truncate">{member.email} · {ROLE_LABELS[member.role] || member.role}{member.city ? ` · ${member.city}` : ""}</div></div>
                <Button variant="outline" size="sm" onClick={() => { setMember(null); setMemberQ(""); }} data-testid="email-member-clear"><X size={14} /></Button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input value={memberQ} onChange={(e) => setMemberQ(e.target.value)} className="pl-8" placeholder="Ex : Dupont ou jean@…" data-testid="email-member-search" />
                </div>
                {memberResults.length > 0 && (
                  <div className="mt-2 border border-slate-200 rounded-xl divide-y divide-slate-100 max-h-56 overflow-auto" data-testid="email-member-results">
                    {memberResults.map((m) => (
                      <button key={m.id} onClick={() => setMember(m)} data-testid={`email-member-opt-${m.id}`} className="w-full text-left px-3 py-2 hover:bg-slate-50">
                        <div className="font-semibold text-sm">{m.name}</div>
                        <div className="text-xs text-slate-500">{m.email} · {ROLE_LABELS[m.role] || m.role}{m.city ? ` · ${m.city}` : ""}</div>
                      </button>
                    ))}
                  </div>
                )}
                {memberQ.trim().length >= 2 && memberResults.length === 0 && <p className="text-xs text-slate-400 mt-2">Aucun membre trouvé.</p>}
              </>
            )}
          </div>
        )}

        {source === "list" && (
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1.5">Adresses e-mail (une par ligne) ou import CSV</label>
            <Textarea rows={5} value={emails} onChange={(e) => setEmails(e.target.value)} data-testid="email-list" placeholder="jean@exemple.fr&#10;marie@exemple.fr" />
            <div className="flex items-center gap-3 mt-2">
              <input ref={csvRef} type="file" accept=".csv,text/csv,text/plain" onChange={onCsv} className="hidden" data-testid="email-csv-input" />
              <Button variant="outline" size="sm" onClick={() => csvRef.current?.click()} data-testid="email-csv-btn"><UploadCloud size={14} className="mr-1.5" /> Importer un CSV</Button>
              <span className="text-xs text-slate-500">{uniqueEmails.length} adresse(s) valide(s)</span>
            </div>
          </div>
        )}

        {source === "contacts" && (
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1.5">Catégorie de contacts (non membres)</label>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => { setContactCat(""); loadContacts(""); }} data-testid="email-contact-cat-all" className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${contactCat === "" ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200"}`}>Tous ({contacts.total || 0})</button>
              {CONTACT_CATS.map((c) => (
                <button key={c.value} onClick={() => { setContactCat(c.value); loadContacts(c.value); }} data-testid={`email-contact-cat-${c.value}`} className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${contactCat === c.value ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200"}`}>{c.label} ({contacts.counts[c.value] || 0})</button>
              ))}
            </div>
            <button onClick={() => { setShowContacts((v) => !v); if (!showContacts) loadContacts(contactCat); }} className="text-xs font-semibold text-[#E05638] mt-2 inline-flex items-center gap-1" data-testid="email-contacts-manage">{showContacts ? "Masquer la gestion" : "Gérer les contacts"}</button>
            {showContacts && (
              <div className="mt-2 border border-slate-200 rounded-xl p-3">
                <div className="flex flex-wrap gap-2 items-end mb-2">
                  <Input value={newContact.email} onChange={(e) => setNewContact((s) => ({ ...s, email: e.target.value }))} placeholder="email@exemple.fr" className="flex-1 min-w-[160px]" data-testid="email-contact-new-email" />
                  <Input value={newContact.name} onChange={(e) => setNewContact((s) => ({ ...s, name: e.target.value }))} placeholder="Nom (optionnel)" className="flex-1 min-w-[120px]" data-testid="email-contact-new-name" />
                  <select value={newContact.category} onChange={(e) => setNewContact((s) => ({ ...s, category: e.target.value }))} className="border border-slate-200 rounded-lg px-2 py-2 text-sm bg-white" data-testid="email-contact-new-cat">{CONTACT_CATS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}</select>
                  <Button size="sm" onClick={addContact} data-testid="email-contact-add">Ajouter</Button>
                </div>
                <div className="divide-y divide-slate-100 max-h-56 overflow-auto">
                  {contacts.items.length === 0 && <div className="text-xs text-slate-400 p-2">Aucun contact. Les adresses libres auxquelles vous écrivez sont enregistrées ici automatiquement.</div>}
                  {contacts.items.map((c) => (
                    <div key={c.id} className="flex items-center gap-2 py-1.5 text-sm" data-testid={`email-contact-${c.id}`}>
                      <div className="min-w-0 flex-1 truncate">{c.email}{c.name ? ` · ${c.name}` : ""}</div>
                      <select value={c.category} onChange={(e) => setContactCategory(c, e.target.value)} className="border border-slate-200 rounded-lg px-1.5 py-1 text-xs bg-white" data-testid={`email-contact-cat-sel-${c.id}`}>{CONTACT_CATS.map((cc) => <option key={cc.value} value={cc.value}>{cc.label}</option>)}</select>
                      <button onClick={() => removeContact(c.id)} className="text-rose-500 hover:text-rose-700" data-testid={`email-contact-del-${c.id}`}><X size={14} /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="rounded-xl border border-slate-200 p-3">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-600 mb-1.5"><Clock size={15} className="text-[#E05638]" /> {frequency ? "Premier envoi + récurrence" : "Programmer l'envoi (optionnel)"}</label>
          <div className="flex flex-wrap items-center gap-2">
            <input type="datetime-local" min={minDateTime} value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm" data-testid="email-schedule-at" />
            <select value={frequency} onChange={(e) => setFrequency(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white" data-testid="email-frequency">
              {FREQ_OPTS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
            {(scheduledAt || frequency) && <Button variant="ghost" size="sm" onClick={() => { setScheduledAt(""); setFrequency(""); }} data-testid="email-schedule-clear"><X size={14} className="mr-1" /> Envoyer immédiatement</Button>}
          </div>
          <p className="text-xs text-slate-400 mt-1">{frequency ? "La campagne partira automatiquement à chaque échéance jusqu'à sa mise en pause." : "Laissez vide pour envoyer tout de suite. L'envoi programmé part automatiquement à l'heure choisie (traitement plusieurs fois par jour)."}</p>
        </div>

        <Button onClick={openConfirm} disabled={sending} className="btn-shine text-white w-full sm:w-auto" data-testid="email-send">
          {sending ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Envoi en cours…</> : (frequency ? <><RefreshCw size={15} className="mr-1.5" /> Créer la campagne récurrente</> : (scheduledAt ? <><Clock size={15} className="mr-1.5" /> Programmer l'envoi</> : <><Send size={15} className="mr-1.5" /> Envoyer l'e-mail</>))}
        </Button>

        {run && (
          <div className={`mt-2 rounded-xl border p-3 text-sm ${run.status === "done" && (run.sent || 0) === 0 && (run.failed || 0) > 0 ? "border-rose-200 bg-rose-50" : "border-slate-200 bg-slate-50"}`} data-testid="email-progress">
            <div className="flex items-center gap-2 font-semibold">
              {run.status !== "done"
                ? <><Loader2 size={16} className="animate-spin text-[#E05638]" /> Envoi en cours…</>
                : ((run.sent || 0) === 0 && (run.failed || 0) > 0)
                  ? <><AlertTriangle size={16} className="text-rose-600" /> <span className="text-rose-700">Échec de l'envoi</span></>
                  : <><CheckCircle2 size={16} className="text-emerald-600" /> Campagne terminée</>}
            </div>
            <p className="text-slate-600 mt-1">{run.sent || 0} / {run.total || 0} envoyé(s){run.failed ? ` · ${run.failed} échec(s)` : ""}</p>
          </div>
        )}

        {scheduledList.length > 0 && (
          <div className="mt-2 rounded-xl border border-slate-200 p-3" data-testid="email-scheduled-list">
            <div className="text-sm font-semibold flex items-center gap-1.5 mb-2"><Clock size={15} className="text-[#E05638]" /> Envois programmés ({scheduledList.length})</div>
            <div className="space-y-1.5">
              {scheduledList.map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`email-scheduled-${s.id}`}>
                  <div className="min-w-0 truncate"><span className="font-medium">{s.subject}</span> <span className="text-xs text-slate-500">· {new Date(s.scheduled_at).toLocaleString("fr-FR")} · {s.total} dest.</span></div>
                  <button onClick={() => cancelScheduled(s.id)} className="text-rose-500 hover:text-rose-700 text-xs font-semibold shrink-0" data-testid={`email-scheduled-cancel-${s.id}`}>Annuler</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {recurringList.length > 0 && (
          <div className="mt-2 rounded-xl border border-slate-200 p-3" data-testid="email-recurring-list">
            <div className="text-sm font-semibold flex items-center gap-1.5 mb-2"><RefreshCw size={15} className="text-[#E05638]" /> Campagnes récurrentes ({recurringList.length})</div>
            <div className="space-y-1.5">
              {recurringList.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`email-recurring-${c.id}`}>
                  <div className="min-w-0 truncate">
                    <span className="font-medium">{c.name}</span>
                    <span className="text-xs text-slate-500"> · {FREQ_LABELS[c.frequency] || c.frequency} · prochain : {new Date(c.next_run).toLocaleString("fr-FR")}{c.status === "paused" ? " · ⏸ en pause" : ""}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => toggleRecurring(c)} className="text-xs font-semibold text-slate-600 hover:text-[#E05638]" data-testid={`email-recurring-toggle-${c.id}`}>{c.status === "active" ? "Pause" : "Reprendre"}</button>
                    <button onClick={() => deleteRecurring(c.id)} className="text-xs font-semibold text-rose-500 hover:text-rose-700" data-testid={`email-recurring-del-${c.id}`}>Supprimer</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Confirmation */}
      {confirm && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="email-confirm-modal">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center gap-2 text-amber-600"><AlertTriangle size={20} /><span className="font-bold">{frequency ? "Confirmer la campagne récurrente" : (scheduledAt ? "Confirmer la programmation" : "Confirmer l'envoi")}</span></div>
            <p className="text-sm text-slate-600 mt-3">
              Vous allez {frequency ? "programmer une campagne récurrente vers" : (scheduledAt ? "programmer" : "envoyer")} {frequency ? "" : "cet e-mail à "}<strong>{source === "filter" ? `jusqu'à ${estimate()}` : estimate()}</strong> destinataire(s)
              {source === "member" && member ? <> (<strong>{member.email}</strong>)</> : null}.
              {frequency && <span className="block text-xs text-[#E05638] font-semibold mt-1">Fréquence : {FREQ_LABELS[frequency]} · 1er envoi le {new Date(scheduledAt).toLocaleString("fr-FR")}.</span>}
              {!frequency && scheduledAt && <span className="block text-xs text-[#E05638] font-semibold mt-1">Programmé pour le {new Date(scheduledAt).toLocaleString("fr-FR")}.</span>}
              {source === "filter" && <span className="block text-xs text-slate-400 mt-1">Le total exact exclut les désinscrits et les comptes sans e-mail.</span>}
            </p>
            <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
              <div className="font-semibold text-slate-800">{subject}</div>
              <div className="text-xs text-slate-500 mt-0.5">Thème : {EMAIL_THEMES.find((t) => t.value === theme)?.label}{banner ? " · bannière" : ""}{images.length ? ` · ${images.length} photo(s)` : ""}{files.length ? ` · ${files.length} fichier(s)` : ""}</div>
            </div>
            <div className="flex gap-2 justify-end mt-5">
              <Button variant="outline" onClick={() => setConfirm(false)} data-testid="email-confirm-cancel">Annuler</Button>
              <Button className="btn-shine text-white" onClick={send} data-testid="email-confirm-ok"><Send size={14} className="mr-1.5" /> {frequency ? "Créer la campagne" : (scheduledAt ? "Programmer" : "Confirmer l'envoi")}</Button>
            </div>
          </div>
        </div>
      )}

      {preview.open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="email-preview-modal" onClick={() => setPreview((p) => ({ ...p, open: false }))}>
          <div className="bg-white rounded-2xl p-4 max-w-3xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-bold flex items-center gap-2"><Eye size={18} className="text-[#E05638]" /> Aperçu de l'e-mail</div>
              <div className="flex items-center gap-2">
                <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                  <button onClick={() => setPreview((p) => ({ ...p, device: "desktop" }))} className={`px-3 py-1.5 text-sm font-semibold flex items-center gap-1 ${preview.device === "desktop" ? "bg-[#E05638] text-white" : "bg-white text-slate-600"}`} data-testid="email-preview-desktop"><Monitor size={14} /> Ordi</button>
                  <button onClick={() => setPreview((p) => ({ ...p, device: "mobile" }))} className={`px-3 py-1.5 text-sm font-semibold flex items-center gap-1 ${preview.device === "mobile" ? "bg-[#E05638] text-white" : "bg-white text-slate-600"}`} data-testid="email-preview-mobile"><Smartphone size={14} /> Mobile</button>
                </div>
                <Button variant="outline" size="sm" onClick={() => setPreview((p) => ({ ...p, open: false }))} data-testid="email-preview-close"><X size={14} /></Button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-slate-100 rounded-xl grid place-items-center p-3">
              {preview.loading
                ? <Loader2 className="animate-spin text-slate-400" />
                : <iframe title="aperçu" srcDoc={preview.html} className="bg-white border border-slate-200 shadow-sm" style={{ width: preview.device === "mobile" ? 375 : 680, height: "70vh", maxWidth: "100%" }} data-testid="email-preview-frame" />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ====================== AGENT BLOG ====================== */
function BlogAgent() {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center max-w-xl mx-auto">
      <Newspaper size={40} className="mx-auto text-[#E05638]" />
      <h3 className="font-semibold text-lg mt-3">Générateur d'articles de blog SEO local</h3>
      <p className="text-sm text-slate-600 mt-2">
        L'agent Blog dispose de son propre atelier complet : choix des mots-clés par IA, rédaction d'article
        (ville × thème), image de couverture et images de corps, publication et blog automatique planifié.
      </p>
      <Link to="/admin/blog">
        <Button className="btn-shine text-white mt-4" data-testid="blog-agent-link">Ouvrir l'atelier Blog <ArrowRight size={15} className="ml-1.5" /></Button>
      </Link>
    </div>
  );
}

/* ====================== AGENT STRATÉGIE (conseiller) ====================== */
function AdvisorAgent() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    const c = endRef.current?.parentElement;
    if (c) c.scrollTop = c.scrollHeight;
  }, [messages, busy]);

  const ask = async () => {
    const q = input.trim();
    if (!q) return;
    const next = [...messages, { role: "user", content: q }];
    setMessages(next); setInput(""); setBusy(true);
    try {
      const { data } = await api.post("/admin/ai-studio/advisor", { messages: next });
      setMessages([...next, { role: "assistant", content: data.reply || "" }]);
    } catch (e) { toast.error(formatApiError(e)); setMessages(messages); }
    finally { setBusy(false); }
  };

  const SUGGESTIONS = [
    "Comment augmenter le nombre d'inscriptions gratuites de particuliers ?",
    "Quelle campagne lancer pour recruter 20 nouveaux commerçants ce mois-ci ?",
    "Idées de contenus pour fidéliser les membres de CSE.",
  ];

  return (
    <div className="bg-white rounded-2xl border border-slate-200 flex flex-col max-w-3xl mx-auto" style={{ height: "60vh" }}>
      <div className="px-5 py-3 border-b border-slate-100 font-semibold flex items-center gap-2"><Lightbulb size={18} className="text-[#E05638]" /> Conseiller marketing</div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="advisor-messages">
        {messages.length === 0 && (
          <div className="text-center text-slate-400 py-8">
            <Bot size={34} className="mx-auto mb-2" />
            <p className="text-sm">Posez une question stratégique sur l'acquisition, la fidélisation ou la conversion.</p>
            <div className="flex flex-col items-center gap-2 mt-4">
              {SUGGESTIONS.map((s, i) => (
                <button key={i} onClick={() => setInput(s)} data-testid={`advisor-suggestion-${i}`} className="text-xs text-[#E05638] bg-orange-50 border border-orange-200 rounded-full px-3 py-1.5 hover:bg-orange-100">{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "assistant" && <div className="shrink-0 w-7 h-7 rounded-full bg-[#E05638] text-white grid place-items-center"><Bot size={15} /></div>}
            <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-line ${m.role === "user" ? "bg-[#E05638] text-white" : "bg-slate-100 text-slate-700"}`}>{m.content}</div>
            {m.role === "user" && <div className="shrink-0 w-7 h-7 rounded-full bg-slate-700 text-white grid place-items-center"><UserIcon size={15} /></div>}
          </div>
        ))}
        {busy && <div className="flex gap-2"><div className="shrink-0 w-7 h-7 rounded-full bg-[#E05638] text-white grid place-items-center"><Bot size={15} /></div><div className="bg-slate-100 rounded-2xl px-3.5 py-2.5"><Loader2 size={16} className="animate-spin text-slate-400" /></div></div>}
        <div ref={endRef} />
      </div>
      <div className="p-3 border-t border-slate-100 flex gap-2">
        <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !busy) ask(); }} data-testid="advisor-input" placeholder="Votre question marketing…" />
        <Button onClick={ask} disabled={busy || !input.trim()} className="btn-shine text-white" data-testid="advisor-send"><Send size={15} /></Button>
      </div>
    </div>
  );
}

/* ====================== HISTORIQUE ====================== */
const HIST_META = {
  email: { label: "E-mail", Icon: Mail, cls: "bg-orange-100 text-[#E05638]" },
  draft: { label: "Brouillon", Icon: Sparkles, cls: "bg-indigo-100 text-indigo-700" },
  image: { label: "Image", Icon: ImageIcon, cls: "bg-sky-100 text-sky-700" },
  social: { label: "Publication", Icon: Megaphone, cls: "bg-emerald-100 text-emerald-700" },
};
const HIST_FILTERS = [
  { value: "", label: "Tout" },
  { value: "email", label: "E-mails" },
  { value: "draft", label: "Brouillons" },
  { value: "image", label: "Images" },
  { value: "social", label: "Publications" },
];

function HistoryAgent({ active, onReuse }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const openDetail = async (h) => {
    const rid = (h.meta || {}).run_id;
    if (!rid) { toast.message("Statistiques disponibles après l'envoi."); return; }
    setDetail({ loading: true, data: null, subject: h.title });
    try { const { data } = await api.get(`/admin/ai-studio/email/campaign/${rid}/detail`); setDetail({ loading: false, data, subject: h.title }); }
    catch (e) { toast.error(formatApiError(e)); setDetail(null); }
  };

  const reuseItem = (h) => {
    const r = (h.meta || {}).reuse;
    if (!r) { toast.message("Rien à réutiliser pour cet élément."); return; }
    if (h.type === "image") onReuse("image", r);
    else if (h.type === "social") onReuse("social", r);
    else onReuse("email", r);
  };

  const load = () => {
    setLoading(true);
    api.get(`/admin/ai-studio/history${filter ? `?type=${filter}` : ""}`)
      .then((r) => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { if (active) load(); /* eslint-disable-next-line */ }, [filter, active]);

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette entrée de l'historique ?")) return;
    try { await api.delete(`/admin/ai-studio/history/${id}`); setItems((p) => p.filter((x) => x.id !== id)); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-semibold flex items-center gap-2"><HistoryIcon size={18} className="text-[#E05638]" /> Historique de vos actions IA</h3>
        <Button variant="outline" size="sm" onClick={load} data-testid="history-refresh">{loading ? <Loader2 size={14} className="animate-spin" /> : "Actualiser"}</Button>
      </div>
      <div className="flex flex-wrap gap-2 mt-4" data-testid="history-filters">
        {HIST_FILTERS.map((f) => (
          <button key={f.value} onClick={() => setFilter(f.value)} data-testid={`history-filter-${f.value || "all"}`}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${filter === f.value ? "bg-[#E05638] text-white border-[#E05638]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{f.label}</button>
        ))}
      </div>

      <div className="mt-4 space-y-2.5" data-testid="history-list">
        {items.length === 0 && !loading && <p className="text-sm text-slate-400 text-center py-10" data-testid="history-empty">Aucune action pour l'instant. Vos e-mails, brouillons, images et publications apparaîtront ici.</p>}
        {items.map((h) => {
          const m = HIST_META[h.type] || HIST_META.draft;
          const meta = h.meta || {};
          return (
            <div key={h.id} className="flex items-start gap-3 border border-slate-200 rounded-xl p-3" data-testid={`history-item-${h.id}`}>
              {h.type === "image" && meta.image_url
                ? <img src={meta.image_url} alt="" className="w-14 h-14 rounded-lg object-cover border border-slate-200 shrink-0" />
                : <div className={`w-14 h-14 rounded-lg grid place-items-center shrink-0 ${m.cls}`}><m.Icon size={22} /></div>}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${m.cls}`}>{m.label}</span>
                  <span className="text-xs text-slate-400">{fmt(h.created_at)}</span>
                </div>
                <div className="font-semibold text-sm text-slate-800 mt-1 break-words">{h.title}</div>
                {h.type === "email" && <div className="text-xs text-slate-500 mt-0.5">{meta.total ?? 0} destinataire(s){typeof meta.sent === "number" ? <span className={meta.failed ? "text-rose-600 font-medium" : "text-emerald-600 font-medium"}> · {meta.sent} envoyé(s){meta.failed ? `, ${meta.failed} échec(s)` : ""}</span> : ""} · thème {EMAIL_THEMES.find((t) => t.value === meta.theme)?.label || meta.theme}{meta.recurring ? " · récurrent" : ""}{meta.n_images ? ` · ${meta.n_images} photo(s)` : ""}{meta.n_files ? ` · ${meta.n_files} fichier(s)` : ""}</div>}
                {h.type === "email" && typeof meta.opens_unique === "number" && (meta.sent || 0) > 0 && (
                  <div className="text-xs mt-1 flex flex-wrap gap-3" data-testid={`history-stats-${h.id}`}>
                    <span className="inline-flex items-center gap-1 text-sky-700"><MailOpen size={12} /> {meta.opens_unique} ouverture(s) ({Math.min(100, Math.round((meta.opens_unique / meta.sent) * 100))}%)</span>
                    <span className="inline-flex items-center gap-1 text-emerald-700"><MousePointerClick size={12} /> {meta.clicks_unique} clic(s) ({Math.min(100, Math.round((meta.clicks_unique / meta.sent) * 100))}%)</span>
                  </div>
                )}
                {h.type === "draft" && meta.body && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{meta.body}</p>}
                {h.type === "social" && meta.text && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{meta.text}</p>}
              </div>
              <div className="flex flex-col items-end gap-1.5 shrink-0">
                {h.type === "email" && (h.meta || {}).run_id && <button onClick={() => openDetail(h)} className="text-xs font-semibold text-sky-700 hover:underline inline-flex items-center gap-1" data-testid={`history-detail-${h.id}`}><BarChart3 size={12} /> Détails</button>}
                {(h.meta || {}).reuse && <button onClick={() => reuseItem(h)} className="text-xs font-semibold text-[#E05638] hover:underline inline-flex items-center gap-1" data-testid={`history-reuse-${h.id}`}><RefreshCw size={12} /> Réutiliser</button>}
                <button onClick={() => remove(h.id)} className="text-slate-400 hover:text-rose-500" data-testid={`history-del-${h.id}`}><Trash2 size={16} /></button>
              </div>
            </div>
          );
        })}
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="history-detail-modal" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl p-5 max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <div className="font-bold flex items-center gap-2"><BarChart3 size={18} className="text-[#E05638]" /> Détail de la campagne</div>
              <Button variant="outline" size="sm" onClick={() => setDetail(null)} data-testid="history-detail-close"><X size={14} /></Button>
            </div>
            <div className="text-sm text-slate-500 mb-3 truncate">{detail.subject}</div>
            {detail.loading ? <div className="py-12 text-center"><Loader2 className="inline animate-spin text-slate-400" /></div> : detail.data && (
              <>
                <div style={{ width: "100%", height: 200 }} data-testid="history-detail-chart">
                  <ResponsiveContainer>
                    <BarChart data={[
                      { name: "Envoyés", v: detail.data.sent },
                      { name: "Échecs", v: detail.data.failed_count || 0 },
                      { name: "Ouverts", v: detail.data.opened_count },
                      { name: "Clics", v: detail.data.clicked_count },
                      { name: "Désinsc.", v: detail.data.unsub_count },
                    ]}>
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="v" radius={[6, 6, 0, 0]}>
                        {["#64748B", "#DC2626", "#0EA5E9", "#10B981", "#F59E0B"].map((col, i) => <Cell key={i} fill={col} />)}
                        <LabelList dataKey="v" position="top" style={{ fontSize: 12, fill: "#334155" }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                {detail.data.fail_reason && (detail.data.failed_count > 0) && (
                  <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900" data-testid="history-detail-fail-reason">
                    <div className="font-semibold mb-0.5">Pourquoi certains envois ont échoué</div>
                    {(() => {
                      const r = detail.data.fail_reason || "";
                      if (r.startsWith("daily_limit_reached")) return "Limite journalière d'envoi atteinte pour aujourd'hui. Augmentez-la dans Buy Credits → Universal Key. Chaque destinataire compte pour 1 envoi, les envois reprennent automatiquement une fois la limite relevée.";
                      if (r.startsWith("insufficient_credits")) return "Crédits insuffisants pour terminer l'envoi. Ajoutez des crédits (Buy Credits → Universal Key) ou activez le rechargement automatique.";
                      if (r.startsWith("integration_disabled")) return "L'intégration e-mail est désactivée. Réactivez-la dans Intégrations (Emergent).";
                      return `Certains envois ont échoué : ${r}`;
                    })()}
                  </div>
                )}
                <div className="mt-3 border border-slate-200 rounded-xl divide-y divide-slate-100 max-h-64 overflow-auto">
                  {detail.data.recipients.length === 0 && <div className="p-4 text-center text-sm text-slate-400">Détail par destinataire indisponible (campagne envoyée avant la mise à jour du suivi).</div>}
                  {detail.data.recipients.map((r, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 text-sm" data-testid={`history-detail-row-${i}`}>
                      <span className="truncate">{r.email}</span>
                      <span className="flex items-center gap-1.5 shrink-0">
                        {r.failed && <span data-testid={`history-detail-failed-${i}`} className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-100 text-rose-700">échec</span>}
                        {r.opened && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-100 text-sky-700">ouvert</span>}
                        {r.clicked && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700">cliqué</span>}
                        {r.unsubscribed && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700">désinscrit</span>}
                        {!r.failed && !r.opened && !r.clicked && !r.unsubscribed && <span className="text-xs text-slate-400">—</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ====================== PAGE ====================== */
const TABS = [
  { value: "social", label: "Réseaux sociaux", Icon: Megaphone },
  { value: "image", label: "Image", Icon: ImageIcon },
  { value: "email", label: "E-mails", Icon: Mail },
  { value: "blog", label: "Blog", Icon: Newspaper },
  { value: "advisor", label: "Stratégie", Icon: Lightbulb },
  { value: "history", label: "Historique", Icon: HistoryIcon },
];

export default function AdminAIStudio() {
  const [tab, setTab] = useState("social");
  const [reuse, setReuse] = useState(null);
  const triggerReuse = (target, data) => { setReuse({ target, data, ts: Date.now() }); setTab(target); };
  return (
    <div>
      <div className="mb-5">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold flex items-center gap-2" data-testid="ai-studio-title">
          <Sparkles className="text-[#E05638]" /> Studio IA
        </h1>
        <p className="text-sm text-slate-500 mt-1">6 assistants marketing : réseaux sociaux, visuels, e-mails, blog, stratégie et historique.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="flex flex-wrap h-auto gap-1 bg-slate-100 p-1" data-testid="ai-studio-tabs">
          {TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value} data-testid={`ai-tab-${t.value}`} className="data-[state=active]:bg-white data-[state=active]:text-[#E05638] gap-1.5">
              <t.Icon size={15} /> {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {/* Panneaux montés en permanence (mémoire des onglets) : seul l'onglet actif est visible */}
      <div className={`mt-6 ${tab === "social" ? "" : "hidden"}`}><SocialAgent reuse={reuse?.target === "social" ? reuse : null} onConsumed={() => setReuse(null)} /></div>
      <div className={`mt-6 ${tab === "image" ? "" : "hidden"}`}><ImageAgent reuse={reuse?.target === "image" ? reuse : null} onConsumed={() => setReuse(null)} /></div>
      <div className={`mt-6 ${tab === "email" ? "" : "hidden"}`}><EmailAgent reuse={reuse?.target === "email" ? reuse : null} onConsumed={() => setReuse(null)} /></div>
      <div className={`mt-6 ${tab === "blog" ? "" : "hidden"}`}><BlogAgent /></div>
      <div className={`mt-6 ${tab === "advisor" ? "" : "hidden"}`}><AdvisorAgent /></div>
      <div className={`mt-6 ${tab === "history" ? "" : "hidden"}`}><HistoryAgent active={tab === "history"} onReuse={triggerReuse} /></div>
    </div>
  );
}
